import type { SchemaFieldNode } from '../types/api';

// Client-side field inference from a sample JSON/XML payload — the same machinery
// MessageSchemaManagerPage uses to build a schema's field tree from a pasted sample, extracted
// here so DocumentGenerateForm (the Generate page) can reuse it to drive a guided form instead
// of a raw-JSON-only textarea. See MessageSchemaManagerPage's field-inference help text: this
// is a single-sample heuristic (required is inferred from presence in the one sample, not a
// real contract), not real schema validation.

export function parseJson(value: string): unknown | null {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function flattenObject(value: unknown, prefix = ''): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).flatMap(([key, entry]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) return Object.entries(flattenObject(entry, path));
    if (Array.isArray(entry)) return [[path, JSON.stringify(entry)]];
    return [[path, entry]];
  }));
}

export function inferType(value: unknown): SchemaFieldNode['type'] {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'string') return 'string';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'unknown';
}

export function objectToFields(value: unknown, path: string): SchemaFieldNode[] {
  if (!value || typeof value !== 'object') return [];
  const entries = Array.isArray(value) ? Object.entries(value[0] ?? {}) : Object.entries(value);
  return entries.map(([key, entry]) => {
    const currentPath = Array.isArray(value) ? `${path}[].${key}` : `${path}.${key}`;
    const type = inferType(entry);
    return {
      path: currentPath,
      name: key,
      type,
      required: entry !== undefined && entry !== null && entry !== '',
      example: entry,
      children: type === 'object' || type === 'array' ? objectToFields(Array.isArray(entry) ? entry[0] : entry, currentPath) : undefined,
    };
  });
}

export function inferXmlFields(sample: string): SchemaFieldNode[] {
  const tags = Array.from(sample.matchAll(/<([A-Za-z0-9_-]+)(\s|>)/g)).map((match) => match[1]).filter((tag) => !tag.startsWith('/'));
  return Array.from(new Set(tags)).map((tag) => ({ path: `source.${tag}`, name: tag, type: 'string', required: true }));
}

export function inferFields(sample: string, format: 'JSON' | 'XML'): SchemaFieldNode[] {
  if (format === 'XML') return inferXmlFields(sample);
  const parsed = parseJson(sample);
  if (!parsed) return [];
  return objectToFields(parsed, 'source');
}
